import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { reservationHostList, reservationHostListPending, reservationConfirm, reservationArrive, reservationNoShow, reservationCancel, type Reservation } from "../../lib/api";
import { useConfirmDialog } from "../../components/ConfirmDialog";

const ROOM_LABEL: Record<string, string> = { any: "Any", lounge: "Lounge", library: "Library", parlor: "Parlor" };
const ROOM_COLOR: Record<string, string> = { lounge: "#7EB8F7", parlor: "#D49BC4", library: "#7EC8A4" };
function slotTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}
function guestName(r: Reservation): string { return r.first_name_at_intake ? `${r.first_name_at_intake} · ${r.party_size}` : `Party of ${r.party_size}`; }
function unactivated(r: Reservation): boolean {
  return ["requested", "confirmed", "reminded"].includes(r.status) && !r.queue_activated_at && !r.waitlist_entry_id && !r.arrived_at;
}

export default function UpcomingReservationsRail({ onArrived, selectedDay, todayDay, roomFilter, onCounts, onRequestCount }: { onArrived?: () => void; selectedDay: string; todayDay: string; roomFilter: string | null; onCounts: (counts: Record<string, number> | null) => void; onRequestCount: (count: number | null) => void }) {
  const [requests, setRequests] = useState<Reservation[]>([]);
  const [dataDay, setDataDay] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const mounted = useRef(true);
  const locks = useRef(new Set<string>());
  const revision = useRef(0);
  const sequence = useRef(0);
  const selectedRef = useRef("");
  selectedRef.current = selectedDay;

  const poll = useCallback(async () => {
    const seq = ++sequence.current;
    const startedRevision = revision.current;
    let pendingLoaded = false;
    try {
      const pend = await reservationHostListPending();
      if (!mounted.current || seq !== sequence.current || startedRevision !== revision.current || locks.current.size) return;
      const pending = [...new Map((pend.reservations ?? []).filter(unactivated).map(r => [r.id, r])).values()];
      const counts: Record<string, number> = {};
      for (const r of pending) counts[r.business_day] = (counts[r.business_day] || 0) + 1;
      onCounts(counts); pendingLoaded = true;
      const day = selectedDay;
      if (!day) return;
      const res = await reservationHostList(day);
      if (!mounted.current || seq !== sequence.current || startedRevision !== revision.current || locks.current.size || selectedRef.current !== day) return;
      setDataDay(day);
      setRequests([...new Map((res.reservations ?? []).filter(r => r.business_day === day && unactivated(r)).map(r => [r.id, r])).values()]);
      setError(null);
      setLoaded(true);
    } catch (e) {
      if (mounted.current && seq === sequence.current && startedRevision === revision.current && !locks.current.size) {
        if (!pendingLoaded) onCounts(null); setError((e as Error).message || "Failed to load waitlist requests"); setLoaded(true);
      }
    }
  }, [selectedDay, onCounts]);
  useEffect(() => {
    mounted.current = true;
    setRequests([]); setDataDay(""); setRowError({}); setError(null); setLoaded(false);
    void poll();
    const timer = setInterval(() => void poll(), 15000);
    return () => { mounted.current = false; ++sequence.current; clearInterval(timer); };
  }, [poll]);
  const runAction = useCallback(async (r: Reservation, action: "confirm" | "arrive" | "no_show" | "cancel") => {
    const actionDay = selectedRef.current;
    if (r.business_day !== actionDay || (r.request_type === "scheduled_waitlist" && action !== "cancel") || ((action === "arrive" || action === "no_show") && r.business_day !== todayDay) || locks.current.has(r.id)) return;
    if (action === "no_show" || action === "cancel") {
      const ok = await confirm({ title: action === "no_show" ? "Mark no-show?" : "Cancel waitlist request?", message: `${guestName(r)} at ${slotTime(r.reserved_at)}`, confirmLabel: action === "no_show" ? "No-show" : "Cancel request", danger: true });
      if (!ok || selectedRef.current !== actionDay) return;
    }
    if (locks.current.has(r.id)) return;
    locks.current.add(r.id); ++revision.current; ++sequence.current;
    setBusy(b => ({ ...b, [r.id]: true })); setRowError(e => ({ ...e, [r.id]: "" }));
    try {
      if (action === "confirm") await reservationConfirm(r.id);
      else if (action === "arrive") { const result = await reservationArrive(r.id); if (!result.waitlist_entry_id) throw new Error("Arrival did not return a queue entry. Refresh before retrying."); onArrived?.(); }
      else if (action === "no_show") await reservationNoShow(r.id);
      else await reservationCancel(r.id);
      if (mounted.current && selectedRef.current === actionDay) setRequests(list => list.filter(x => x.id !== r.id));
    } catch (e) {
      if (mounted.current && selectedRef.current === actionDay) setRowError(er => ({ ...er, [r.id]: (e as Error).message || "Action failed" }));
    } finally {
      ++revision.current; locks.current.delete(r.id);
      if (mounted.current) { setBusy(b => ({ ...b, [r.id]: false })); void poll(); }
    }
  }, [confirm, onArrived, poll, todayDay]);

  const current = dataDay === selectedDay ? requests : [];
  const visible = roomFilter ? current.filter(r => r.preferred_room === roomFilter || r.preferred_room === "any") : current;
  const future = selectedDay !== todayDay;
  const visibleError = dataDay && dataDay !== selectedDay ? null : error;
  useEffect(() => { onRequestCount(loaded && dataDay === selectedDay && !error ? visible.length : null); }, [loaded, dataDay, selectedDay, error, visible.length, onRequestCount]);
  if (!future && visible.length === 0 && !visibleError) return confirmDialog;
  return <section style={styles.wrap} aria-label={future ? "Future waitlist requests" : "Scheduled today"}>
    <div style={styles.head}><span style={styles.title}>{future ? "Future waitlist requests" : "Scheduled today"}</span></div>
    {(!loaded || dataDay !== selectedDay && !visibleError) ? <div style={styles.empty}>Loading waitlist requests…</div> : visible.length === 0 ? (!visibleError && <div style={styles.empty}>No waitlist requests for this date.</div>) :
    <div style={future ? styles.futureGrid : styles.rail} role="list" aria-label="Waitlist requests for selected date">{visible.map(r => {
      const scheduled = r.request_type === "scheduled_waitlist";
      const isToday = r.business_day === todayDay;
      const rc = ROOM_COLOR[r.preferred_room] || "var(--muted)";
      return <div key={r.id} style={future ? { ...styles.card, ...styles.futureCard } : styles.card} role="listitem">
        <div style={styles.cardTop}><span style={styles.time}>{slotTime(r.reserved_at)}</span>{r.c33_priority && <span style={styles.c33}>C33</span>}</div>
        <div style={styles.name}>{guestName(r)}</div>
        <div style={styles.meta}><span style={{ ...styles.roomTag, color: rc, borderColor: rc }}>{ROOM_LABEL[r.preferred_room] || r.preferred_room}</span>{r.preferred_seating_label && <span style={styles.seating}>{scheduled ? "Requested: " : ""}{r.preferred_seating_label}</span>}{r.deposit_model !== "none" && <span style={styles.deposit}>{r.payment_state === "authorized" ? "Hold" : r.payment_state === "captured" ? "Paid" : "Deposit"}</span>}</div>
        {r.celebration_notes && <div style={styles.notes}>🎉 {r.celebration_notes}</div>}
        {rowError[r.id] && <div role="alert" style={styles.rowErr}>{rowError[r.id]}</div>}
        {scheduled && (r.activation_error ? <div role="alert" style={styles.rowErr}><strong>Not yet in queue — activation needs attention.</strong><p>{r.activation_error}</p>Review the request and active queue. Contact the guest if their entry is delayed.</div> : <div style={styles.notes}>Joins the queue automatically at the chosen time. No early arrival check-in is needed.</div>)}
        {scheduled && r.preferred_seating_label && <div style={styles.notes}>Seating preference only; subject to availability.</div>}
        <div style={styles.actions}>
          {!scheduled && r.status === "requested" && <button type="button" disabled={busy[r.id]} style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => void runAction(r, "confirm")}>Confirm</button>}
          {!scheduled && isToday && <button type="button" disabled={busy[r.id]} style={styles.btn} onClick={() => void runAction(r, "arrive")}>Arrived</button>}
          {!scheduled && isToday && r.status !== "requested" && <button type="button" disabled={busy[r.id]} style={styles.btn} onClick={() => void runAction(r, "no_show")}>No-show</button>}
          <button type="button" disabled={busy[r.id]} style={styles.btn} onClick={() => void runAction(r, "cancel")}>Cancel</button>
        </div>
      </div>;
    })}</div>}
    {visibleError && <div role="alert" style={styles.err}>{current.length ? "Unable to refresh. Showing last loaded waitlist requests: " : "Unable to load waitlist requests: "}{visibleError}</div>}
    {confirmDialog}
  </section>;
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    borderBottom: "1px solid var(--border)",
    padding: "10px 12px 12px",
    background: "rgba(255,255,255,0.02)",
  },
  controls: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 8 },
  dateInput: { colorScheme: "dark", display: "inline-block", minHeight: 44, maxWidth: "100%", marginLeft: 8, color: "var(--text)", background: "var(--dark-1)", border: "1px solid var(--border)", borderRadius: 4, padding: "4px 8px" },
  dateButton: { minHeight: 44, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 4, background: "var(--dark-1)", color: "var(--text)", cursor: "pointer" },
  selectedDate: { color: "var(--text)", fontSize: 14 },
  shortcuts: { display: "flex", gap: 6, overflowX: "auto", marginBottom: 8 },
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  title: {
    fontFamily: "Georgia, serif", fontSize: 15, fontWeight: 700, color: "var(--gold)",
  },
  titleAmber: {
    fontFamily: "Georgia, serif", fontSize: 15, fontWeight: 700, color: "#F9AF36",
  },
  count: {
    fontSize: 12, fontWeight: 700, color: "var(--muted)",
    border: "1px solid var(--border)", borderRadius: 10, padding: "1px 8px",
  },
  countAmber: {
    fontSize: 12, fontWeight: 700, color: "#F9AF36",
    border: "1px solid rgba(249,175,54,0.5)", background: "rgba(249,175,54,0.1)",
    borderRadius: 10, padding: "1px 8px",
  },
  cardRequest: { borderColor: "rgba(249,175,54,0.45)" },
  dateChip: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
    color: "#F9AF36", background: "rgba(249,175,54,0.1)", borderRadius: 4, padding: "1px 6px",
  },
  err: { color: "#FF8A7A", fontSize: 12, fontWeight: 600 },
  empty: { color: "var(--muted)", fontSize: 13, padding: "6px 2px" },
  rail: { display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 },
  futureGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 10 },
  futureCard: { width: "auto", minWidth: 0 },
  card: {
    minWidth: 190, width: 300, maxWidth: "100%", boxSizing: "border-box", flex: "0 0 auto",
    border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px",
    background: "var(--dark-1)", display: "flex", flexDirection: "column", gap: 5,
  },
  cardTop: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  time: { fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700, color: "var(--text)" },
  c33: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
    color: "var(--gold)", background: "rgba(249,175,54,0.12)", borderRadius: 4, padding: "1px 5px",
  },
  name: { fontSize: 14, color: "var(--text)", fontWeight: 600 },
  meta: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  roomTag: {
    fontSize: 11, fontWeight: 600, border: "1px solid", borderRadius: 4, padding: "1px 6px",
  },
  seating: {
    fontSize: 11, fontWeight: 600, color: "var(--text)", border: "1px solid var(--border)",
    borderRadius: 4, padding: "1px 6px",
  },
  deposit: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
    color: "#7EC8A4", background: "rgba(126,200,164,0.12)", borderRadius: 4, padding: "1px 5px",
  },
  requested: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
    color: "#F9AF36", background: "rgba(249,175,54,0.1)", borderRadius: 4, padding: "1px 5px",
  },
  notes: { fontSize: 12, color: "var(--muted)" },
  rowErr: { fontSize: 11, color: "#FF8A7A", fontWeight: 600 },
  actions: { display: "flex", gap: 6, marginTop: 2 },
  btn: {
    flex: 1, minHeight: 44, fontSize: 12, fontWeight: 600, cursor: "pointer",
    border: "1px solid var(--border)", borderRadius: 4, padding: "5px 6px",
    background: "transparent", color: "var(--muted)",
  },
  btnPrimary: { background: "var(--gold)", color: "var(--dark-1)", border: "1px solid var(--gold)" },
};
